import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {environment} from '../../environments/environment';
import {Factory} from '../model/factory';
import {ModelKey} from '../model/model';
import {ObjectQueryParameters} from '../model/object-query-parameters';
import {PagedList} from '../model/paged-list';

export enum CoolResource {
  Applications = 'applications',
  Clients = 'clients',
  ClientRepresentatives = 'clientRepresentatives',
  Countries = 'countries',
  CreditInsurances = 'creditInsurances',
  EmailLogs = 'emailLogs',
  EmailStates = 'emailStates',
  EmailTemplates = 'emailTemplates',
  EndCustomers = 'endCustomers',
  EndCustomerContacts = 'endCustomerContacts',
  FinancingMethods = 'financingMethods',
  Invoices = 'invoices',
  InvoiceAttachments = 'invoiceAttachments',
  InvoiceAttachmentType = 'invoiceAttachmentTypes',
  InvoiceConfigurations = 'invoiceConfigurations',
  InvoiceStates = 'invoiceStates',
  InvoiceStateLogs = 'invoiceStateLogs',
  LeadInformations = 'leadInformations',
  LeadSources = 'leadSources',
  Lookup = 'lookup',
  NotificationLogs = 'notificationLogs',
  Partners = 'partners',
  PaymentHistories = 'paymentHistories',
  PaymentHistoryImports = 'paymentHistoryImports',
  PaymentTransferStatuses = 'paymentTransferStatuses',
  PenaltyInterestMethods = 'penaltyInterestMethods',
  SmsLogs = 'smsLogs',
  SmsTemplates = 'smsTemplates',
  Users = 'users',
  Validation = 'validation'
}

@Injectable({providedIn: 'root'})
export class CoolService {

  constructor(private http: HttpClient) {
  }

  // Generic

  postResource<T, U>(resource: CoolResource, obj: T, factory: Factory<U>): Observable<U> {
    return this.request<U>('post', resource, true, null, obj).pipe(
      map(res => {
        return factory(res.body);
      }));
  }

  postArrayResource<T, U>(resource: CoolResource, obj: T, factory: Factory<U>): Observable<U[]> {
    return this.request<U[]>('post', resource, true, null, obj).pipe(
      map(res => {
        return res.body.map(o => factory(o));
      }));
  }

  public putResource<T, U>(resource: CoolResource, id: ModelKey, obj: T, factory: Factory<U>): Observable<U> {
    return this.request<U>('put', resource + '/' + id, true, null, obj).pipe(
      map(res => {
        return factory(res.body);
      }));
  }

  patchResourceAction<T, U>(resource: CoolResource, id: number, obj: T, action: string, factory: Factory<U>): Observable<U> {
    return this.request<U>('patch', resource + '/' + id + '/' + action, true, null, obj).pipe(
      map(res => {
        return factory(res.body);
      }));
  }

  public deleteResource<T>(resource: CoolResource, id: ModelKey): Observable<void> {
    return this.request<T>('delete', resource + '/' + id, true, null, null).pipe(
      map(() => {
        return null;
      }));
  }

  public getResource<T>(resource: CoolResource, id: ModelKey, factory: Factory<T>): Observable<T> {
    if (id === null) {
      console.warn("null id for " + resource);
    }
    return this.request<T>('get', resource + '/' + id, true).pipe(
      map(res => {
        return factory(res.body);
      }));
  }

  getResourceWithParams<T>(resource: CoolResource, id: string, params: HttpParams, factory: Factory<T>): Observable<T> {
    return this.request<T>('get', resource + '/' + id, true, params).pipe(
      map(res => {
        return factory(res.body);
      }));
  }

  getArrayResource<T>(resource: CoolResource, parameters: ObjectQueryParameters, factory: Factory<T>): Observable<T[]> {
    return this.request<T[]>('get', resource, true, parameters !== null ? parameters.toHttpParams() : null).pipe(
      map(res => {
        return res.body.map(obj => factory(obj));
      }));
  }

  getArrayResourcePaged<T>(resource: CoolResource, parameters: ObjectQueryParameters, factory: Factory<T>): Observable<PagedList<T>> {
    return this.request<T[]>('get', resource, true, parameters !== null ? parameters.toHttpParams() : null).pipe(
      map(res => {
        return {
          page: Number(res.headers.get('X-Paging-Page')),
          pageSize: Number(res.headers.get('X-Paging-PageSize')),
          total: Number(res.headers.get('X-Paging-TotalSize')),
          elements: res.body.map(obj => factory(obj))
        };
      }));
  }

  getArrayResourceWithParams<T>(resource: CoolResource, id: string, params: HttpParams, factory: Factory<T>): Observable<T[]> {
    return this.request<T[]>('get', resource + '/' + id, true, params).pipe(
      map(res => {
        return res.body.map(obj => factory(obj));
      }));
  }

  private request<T>(method: string, action: string, authorization: boolean, params?: HttpParams, body?: unknown): Observable<HttpResponse<T>> {

    const headers: HttpHeaders = null;
    const url = environment.url.base + '/' + action;

    console.log('CoolService', method, action);

    return this.http.request<T>(method, url, {
      body: body,
      headers: headers,
      observe: 'response',
      params: params
    });
  }
}
